/**
 * The Today card on the Calendar tab: which state it is in, its
 * countdowns, and the day's numbers once the last lesson is over. Pure,
 * so the boundaries are tested without a browser
 * (scripts/verify/today-card.ts).
 */

export interface CardBooking {
  status: string;
  paymentMode: 'credits' | 'cash';
  amountDue: number | null;
  received?: number | null;
  paidAt: string | null;
  lateCancel?: boolean;
}
export interface CardSession {
  id: string; starts_at: string; ends_at: string;
  bookings: CardBooking[];
}

export type CardState<S extends CardSession> =
  | { kind: 'loading' }
  | { kind: 'onCourt'; session: S }
  | { kind: 'next'; session: S }
  | { kind: 'done'; taught: number }
  | { kind: 'empty' };

const ms = (iso: string) => new Date(iso).getTime();

/**
 * Late cancellations are listed so the coach sees who owes, but nobody is
 * coming to them: the card leaves them out, and a lesson left with nobody
 * is not a lesson.
 */
function coming<S extends CardSession>(sessions: S[]): S[] {
  return sessions
    .map((s) => ({ ...s, bookings: s.bookings.filter((b) => !b.lateCancel) }))
    .filter((s) => s.bookings.length > 0);
}

/**
 * On court from the minute it starts until the minute it ends; at the
 * seam of two lessons back to back, the second one. Otherwise the next to
 * start; done once every booked lesson is over; empty when nobody booked.
 */
export function cardState<S extends CardSession>(sessions: S[] | null, now: number): CardState<S> {
  if (sessions === null) return { kind: 'loading' };
  const booked = coming(sessions);
  const onCourt = booked.find((s) => ms(s.starts_at) <= now && ms(s.ends_at) > now);
  if (onCourt) return { kind: 'onCourt', session: onCourt };
  const next = booked.find((s) => ms(s.starts_at) > now);
  if (next) return { kind: 'next', session: next };
  return booked.length ? { kind: 'done', taught: booked.length } : { kind: 'empty' };
}

/** Whole minutes until it starts, rounded up: one until the very start, never «через 0 мин». */
export const minutesUntil = (iso: string, now: number) => Math.max(0, Math.ceil((ms(iso) - now) / 60000));

/** Whole minutes until it ends, rounded up, never negative. */
export const minutesLeft = (iso: string, now: number) => Math.max(0, Math.ceil((ms(iso) - now) / 60000));

/** How far through the lesson, 0 to 1. */
export function progress(s: { starts_at: string; ends_at: string }, now: number): number {
  const from = ms(s.starts_at), to = ms(s.ends_at);
  if (to <= from) return 1;
  return Math.min(1, Math.max(0, (now - from) / (to - from)));
}

/**
 * Still to collect for today's lessons that have started: unpaid cash,
 * less what was already received. The rule of /api/coach/owed and the
 * Money tab — booked or attended only, so a no-show or a late cancel is
 * not counted here.
 */
export function dayToCollect(sessions: CardSession[], now: number): number {
  let sum = 0;
  for (const s of sessions) {
    if (ms(s.starts_at) > now) continue;
    for (const b of s.bookings) {
      if (b.paymentMode !== 'cash' || b.paidAt || (b.status !== 'booked' && b.status !== 'attended')) continue;
      sum += Math.max(0, Number(b.amountDue ?? 0) - Number(b.received ?? 0));
    }
  }
  return Math.round(sum * 100) / 100;
}

/** The first lesson with someone booked (tomorrow's, in the end state). */
export function firstBooked<S extends CardSession>(sessions: S[]): S | null {
  return coming(sessions)[0] ?? null;
}

/** Three players or more: one line «Группа · N игроков» instead of every name. */
export const groupSummary = (players: number) => players >= 3;
